import React, { useState } from 'react';
import { apprenantsAPI, alertesAPI, statsAPI, suivisAPI, configAPI } from '../services/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import Icon from '../components/Icon';
import Modal from '../components/Modal'; // Ajout de l'import Modal
import DateRangePicker from '../components/DateRangePicker'; // Ajout de l'import DateRangePicker

const Rapports = () => {
  // États pour le modal de sélection de période
  const [showDateModal, setShowDateModal] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fonction pour générer le PDF du suivi
  const generateSuiviPDF = async () => {
    try {
      // Récupérer les données
      const [suiviStatsResponse, suivisResponse, apprenantsResponse, configResponse] = await Promise.all([
        statsAPI.getSuivi(),
        suivisAPI.getAll(),
        apprenantsAPI.getAll({ limit: 1000 }),
        configAPI.get()
      ]);

      const suiviStats = suiviStatsResponse.data || {};
      const suivis = suivisResponse.data.suivis || [];
      const apprenants = apprenantsResponse.data.apprenants || [];
      const config = configResponse.data || {};
      
      // Créer un dictionnaire des apprenants pour un accès rapide
      const apprenantsDict = {};
      apprenants.forEach(apprenant => {
        apprenantsDict[apprenant._id] = apprenant;
      });

      const positifs = config.statutsInsertion?.positif || [];
      const delaiSuivi = config.delaiSuivi || 90;

      const doc = new jsPDF();
      const date = format(new Date(), 'dd/MM/yyyy');

      // En-tête
      doc.setFontSize(18);
      doc.setTextColor(220, 38, 38);
      doc.text('SIMPLON Côte d\'Ivoire', 105, 20, { align: 'center' });
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text('Plateforme de Gestion des Insertions', 105, 28, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Généré le: ' + date, 105, 35, { align: 'center' });
      doc.setDrawColor(220, 38, 38);
      doc.line(20, 40, 190, 40);

      // Titre du rapport
      doc.setFontSize(16);
      doc.setTextColor(0);
      doc.text('RAPPORT SUIVI POST-INSERTION', 105, 50, { align: 'center' });

      // 1. STATISTIQUES GÉNÉRALES
      doc.setFontSize(14);
      doc.setTextColor(220, 38, 38);
      doc.text('1. STATISTIQUES DE SUIVI', 20, 65);

      doc.setFontSize(11);
      doc.setTextColor(0);
      
      const statsData = [
        ['Suivis effectués', suiviStats.suiviOK || 0],
        ['En attente', suiviStats.enAttente || 0],
        ['En retard', suiviStats.enRetard || 0],
        ['Note moyenne', suiviStats.noteMoyenne ? suiviStats.noteMoyenne + '/5' : 'N/A'],
        ['Délai de suivi configuré', delaiSuivi + ' jours']
      ];

      let y = 80;
      statsData.forEach(([label, value]) => {
        doc.text(`${label}: ${value}`, 30, y);
        y += 7;
      });

      // 2. APPRENANTS À CONTACTER
      y += 5;
      doc.setFontSize(14);
      doc.setTextColor(220, 38, 38);
      doc.text('2. APPRENANTS À CONTACTER', 20, y);
      y += 10;

      // Calculer les apprenants à contacter
      const maintenant = new Date();
      const aContacter = apprenants.filter(a => {
        if (!a.dateInsertion) return false;
        if (!positifs.includes(a.statutInsertion)) return false;
        
        const dateInsertion = new Date(a.dateInsertion);
        const jours = Math.floor((maintenant - dateInsertion) / (1000 * 60 * 60 * 24));
        const dernierSuivi = suivis
          .filter(s => {
            const apprenantId = s.apprenantId && typeof s.apprenantId === 'object' ? s.apprenantId._id : s.apprenantId;
            return apprenantId === a._id;
          })
          .sort((x, y) => new Date(y.date) - new Date(x.date))[0];
        
        if (dernierSuivi) return false;
        return jours >= delaiSuivi;
      });

      if (aContacter.length > 0) {
        const contactData = aContacter.slice(0, 10).map(a => [
          a.nom || '-',
          a.prenom || '-',
          a.telephone || '-',
          a.entreprise || '-',
          a.dateInsertion ? format(new Date(a.dateInsertion), 'dd/MM/yyyy') : '-'
        ]);

        doc.autoTable({
          startY: y,
          head: [['Nom', 'Prénom', 'Téléphone', 'Entreprise', 'Date insertion']],
          body: contactData,
          theme: 'striped',
          headStyles: { fillColor: [220, 38, 38] },
          styles: { fontSize: 9 }
        });
        
        y = doc.lastAutoTable.finalY + 10;
      } else {
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text('Aucun apprenant à contacter', 30, y);
        y += 15;
      }

      // 3. SUIVIS ENREGISTRÉS (Simples et Multi-niveaux)
      doc.setFontSize(14);
      doc.setTextColor(220, 38, 38);
      doc.text('3. SUIVIS ENREGISTRÉS', 20, y);
      y += 10;

      // Préparer les données des suivis (simples et multi-niveaux)
      const suivisData = [];

      suivis.forEach(suivi => {
        let apprenant = null;
        
        // Récupérer l'apprenant
        if (suivi.apprenantId && typeof suivi.apprenantId === 'object') {
          apprenant = apprenantsDict[suivi.apprenantId._id] || suivi.apprenantId;
        } else if (suivi.apprenantId) {
          apprenant = apprenantsDict[suivi.apprenantId];
        }

        // Si c'est un suivi multi-niveaux
        if (suivi.suivisMultiNiveaux && suivi.suivisMultiNiveaux.length > 0) {
          suivi.suivisMultiNiveaux.forEach(niveau => {
            suivisData.push([
              apprenant?.nom || '-',
              apprenant?.prenom || '-',
              niveau.niveau || 'Multi',
              niveau.dateContact ? format(new Date(niveau.dateContact), 'dd/MM/yyyy') : '-',
              niveau.note || '-',
              niveau.appreciation || '-'
            ]);
          });
        } 
        // Si c'est un suivi simple
        else {
          suivisData.push([
            apprenant?.nom || '-',
            apprenant?.prenom || '-',
            'Simple',
            suivi.date ? format(new Date(suivi.date), 'dd/MM/yyyy') : '-',
            suivi.note || '-',
            suivi.appreciation || '-'
          ]);
        }
      });

      if (suivisData.length > 0) {
        doc.autoTable({
          startY: y,
          head: [['Nom', 'Prénom', 'Type', 'Date', 'Note', 'Appréciation']],
          body: suivisData.slice(0, 20),
          theme: 'striped',
          headStyles: { fillColor: [220, 38, 38] },
          styles: { fontSize: 8 }
        });
        
        y = doc.lastAutoTable.finalY + 10;
      } else {
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text('Aucun suivi enregistré', 30, y);
        y += 15;
      }

      // 4. HISTORIQUE RÉCENT
      if (y > 250) {
        doc.addPage();
        y = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(220, 38, 38);
      doc.text('4. HISTORIQUE RÉCENT', 20, y);
      y += 10;

      const historique = suivis
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 15);

      if (historique.length > 0) {
        const historiqueData = historique.map(suivi => {
          let apprenant = null;
          if (suivi.apprenantId && typeof suivi.apprenantId === 'object') {
            apprenant = apprenantsDict[suivi.apprenantId._id] || suivi.apprenantId;
          } else if (suivi.apprenantId) {
            apprenant = apprenantsDict[suivi.apprenantId];
          }
          
          return [
            apprenant?.nom || '-',
            apprenant?.prenom || '-',
            suivi.date ? format(new Date(suivi.date), 'dd/MM/yyyy') : '-',
            suivi.type === 'telephone' ? 'Téléphone' : suivi.type === 'email' ? 'Email' : 'Visite',
            suivi.note || '-',
            suivi.appreciation || '-'
          ];
        });

        doc.autoTable({
          startY: y,
          head: [['Nom', 'Prénom', 'Date', 'Type', 'Note', 'Appréciation']],
          body: historiqueData,
          theme: 'striped',
          headStyles: { fillColor: [220, 38, 38] },
          styles: { fontSize: 8 }
        });
      } else {
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text('Aucun historique de suivi', 30, y);
      }

      // Sauvegarder le PDF
      doc.save(`SIMPLON_Suivi_${date.replace(/\//g, '-')}.pdf`);
      toast.success('Rapport Suivi PDF généré !');
    } catch (error) {
      console.error('Erreur génération rapport suivi:', error);
      toast.error('Erreur lors de la génération du rapport Suivi');
    }
  };

  // Fonction pour générer le PDF des alertes
  const generateAlertesPDF = async () => {
    try {
      // Récupérer les alertes
      const alertesResponse = await alertesAPI.getAll();
      const alertes = alertesResponse.data.alertes || [];
      const counts = alertesResponse.data.counts || {};

      // Récupérer les apprenants pour avoir les prénoms
      const apprenantsResponse = await apprenantsAPI.getAll({ limit: 1000 });
      const apprenants = apprenantsResponse.data.apprenants || [];

      // Créer un dictionnaire pour accéder rapidement aux apprenants par leur ID
      const apprenantsDict = {};
      apprenants.forEach(app => {
        apprenantsDict[app._id] = app;
      });

      const doc = new jsPDF();
      const date = format(new Date(), 'dd/MM/yyyy');

      // En-tête
      doc.setFontSize(18);
      doc.setTextColor(220, 38, 38);
      doc.text('SIMPLON Côte d\'Ivoire', 105, 20, { align: 'center' });
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text('Plateforme de Gestion des Insertions', 105, 28, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Généré le: ' + date, 105, 35, { align: 'center' });
      doc.setDrawColor(220, 38, 38);
      doc.line(20, 40, 190, 40);

      // Titre du rapport
      doc.setFontSize(16);
      doc.setTextColor(0);
      doc.text('RAPPORT ALERTES', 105, 50, { align: 'center' });

      // Résumé des alertes (KPIs)
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text('RÉSUMÉ DES ALERTES', 20, 65);
      
      doc.setFontSize(11);
      doc.setTextColor(0);
      
      const kpiData = [
        ['Critiques', counts.critique || 0],
        ['Relances', counts.relance || 0],
        ['Suivi 3 mois', counts.suivi || 0],
        ['Vérifications', counts.verif || 0],
        ['TOTAL', alertes.length]
      ];

      let y = 75;
      kpiData.forEach(([label, value]) => {
        doc.text(`${label}: ${value}`, 30, y);
        y += 7;
      });

      // Tableau des alertes détaillées
      if (alertes.length > 0) {
        doc.setFontSize(12);
        doc.setTextColor(100);
        doc.text('DÉTAIL DES ALERTES', 20, y + 5);
        
        const tableData = alertes.map((alerte, index) => {
          const apprenant = apprenantsDict[alerte.apprenantId] || {};
          return [
            index + 1,
            alerte.type === 'critique' ? 'Critique' : 
            alerte.type === 'relance' ? 'Relance' : 
            alerte.type === 'suivi' ? 'Suivi 3 mois' : 'Vérification',
            alerte.titre || '-',
            alerte.message || alerte.msg || '-',
            apprenant.nom || alerte.apprenantNom || '-',
            apprenant.prenom || '-'
          ];
        });

        doc.autoTable({
          startY: y + 10,
          head: [['N°', 'Type', 'Titre', 'Message', 'Nom', 'Prénom']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [220, 38, 38] },
          styles: { fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 20 },
            2: { cellWidth: 30 },
            3: { cellWidth: 50 },
            4: { cellWidth: 25 },
            5: { cellWidth: 25 }
          }
        });
      } else {
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text('Aucune alerte pour le moment', 105, y + 15, { align: 'center' });
      }

      // Sauvegarder le PDF
      doc.save(`SIMPLON_Alertes_${date.replace(/\//g, '-')}.pdf`);
      toast.success('Rapport Alertes PDF généré !');
    } catch (error) {
      console.error('Erreur génération rapport alertes:', error);
      toast.error('Erreur lors de la génération du rapport Alertes');
    }
  };

  // Fonction pour générer le PDF du SPC
  const generateSPCPDF = async () => {
    try {
      // Récupérer les données
      const [apprenantsResponse, configResponse] = await Promise.all([
        apprenantsAPI.getAll({ limit: 1000 }),
        configAPI.get()
      ]);

      const apprenants = apprenantsResponse.data.apprenants || [];
      const config = configResponse.data || {};
      
      // Filtrer les apprenants avec insertion (métiers uniquement)
      const metiers = apprenants.filter(a => {
        if (!a.typeFormation) return false;
        if (a.typeFormation.toLowerCase().includes('acculturation')) return false;
        if (!a.dateFin || !a.dateInsertion) return false;
        return true;
      });

      const doc = new jsPDF();
      const date = format(new Date(), 'dd/MM/yyyy');

      // En-tête
      doc.setFontSize(18);
      doc.setTextColor(220, 38, 38);
      doc.text('SIMPLON Côte d\'Ivoire', 105, 20, { align: 'center' });
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text('Plateforme de Gestion des Insertions', 105, 28, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Généré le: ' + date, 105, 35, { align: 'center' });
      doc.setDrawColor(220, 38, 38);
      doc.line(20, 40, 190, 40);

      // Titre du rapport
      doc.setFontSize(16);
      doc.setTextColor(0);
      doc.text('RAPPORT SPC - CONTRÔLE STATISTIQUE', 105, 50, { align: 'center' });

      // Note sur les formations métiers
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text('(Uniquement sur formations métiers)', 105, 58, { align: 'center' });

      let y = 70;

      if (metiers.length < 30) {
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text('Données insuffisantes pour l\'analyse SPC', 105, y, { align: 'center' });
        y += 10;
        doc.setFontSize(10);
        doc.text(`Minimum 30 observations requises (${metiers.length} disponibles)`, 105, y, { align: 'center' });
      } else {
        // Calculer les délais d'insertion
        const delais = metiers.map(a => {
          const dateFin = new Date(a.dateFin);
          const dateInsertion = new Date(a.dateInsertion);
          return Math.round((dateInsertion - dateFin) / (1000 * 60 * 60 * 24));
        }).filter(d => d >= 0);

        if (delais.length === 0) {
          doc.setFontSize(12);
          doc.setTextColor(0);
          doc.text('Aucun délai d\'insertion valide', 105, y, { align: 'center' });
        } else {
          // Moyenne et écart-type
          const moyenne = delais.reduce((a, b) => a + b, 0) / delais.length;
          const variance = delais.reduce((sum, d) => sum + Math.pow(d - moyenne, 2), 0) / delais.length;
          const ecartType = Math.sqrt(variance);

          // Limites de contrôle (3 sigma)
          const UCL = moyenne + (3 * ecartType);
          const LCL = Math.max(0, moyenne - (3 * ecartType));

          // Capabilité (objectif: ≤ 90 jours)
          const USL = config?.USL || 90;
          const Cp = (USL - 0) / (6 * ecartType);
          const Cpk = Math.min(
            (USL - moyenne) / (3 * ecartType),
            (moyenne - 0) / (3 * ecartType)
          );

          // 1. STATISTIQUES GÉNÉRALES
          doc.setFontSize(14);
          doc.setTextColor(220, 38, 38);
          doc.text('1. STATISTIQUES GÉNÉRALES', 20, y);
          y += 10;

          doc.setFontSize(11);
          doc.setTextColor(0);
          
          const statsData = [
            ['Nombre d\'observations', delais.length],
            ['Moyenne des délais', moyenne.toFixed(1) + ' jours'],
            ['Écart-type', ecartType.toFixed(1) + ' jours'],
            ['UCL (Limite supérieure)', UCL.toFixed(1) + ' jours'],
            ['LCL (Limite inférieure)', LCL.toFixed(1) + ' jours'],
            ['USL (Objectif)', USL + ' jours']
          ];

          statsData.forEach(([label, value]) => {
            doc.text(`${label}: ${value}`, 30, y);
            y += 7;
          });
          y += 5;

          // 2. CAPABILITÉ DU PROCESSUS
          doc.setFontSize(14);
          doc.setTextColor(220, 38, 38);
          doc.text('2. CAPABILITÉ DU PROCESSUS', 20, y);
          y += 10;

          doc.setFontSize(11);
          doc.setTextColor(0);
          
          const capabiliteData = [
            ['Cp', Cp.toFixed(2)],
            ['Cpk', Cpk.toFixed(2)]
          ];

          capabiliteData.forEach(([label, value]) => {
            doc.text(`${label}: ${value}`, 30, y);
            y += 7;
          });
          y += 5;

          // Interprétation du Cpk
          doc.setFontSize(10);
          if (Cpk >= 1.33) {
            doc.setTextColor(34, 197, 94);
            doc.text('✓ Processus capable (Cpk ≥ 1.33)', 30, y);
          } else if (Cpk >= 1.0) {
            doc.setTextColor(245, 158, 11);
            doc.text('⚠ Processus marginalement capable (1.0 ≤ Cpk < 1.33)', 30, y);
          } else {
            doc.setTextColor(220, 38, 38);
            doc.text('✗ Processus non capable (Cpk < 1.0)', 30, y);
          }
          y += 10;

          // 3. DISTRIBUTION DES DÉLAIS
          doc.setFontSize(14);
          doc.setTextColor(220, 38, 38);
          doc.text('3. DISTRIBUTION DES DÉLAIS', 20, y);
          y += 10;

          // Créer des classes pour l'histogramme
          const maxDelai = Math.min(Math.max(...delais), USL * 2);
          const classes = 10;
          const pas = Math.ceil(maxDelai / classes);
          const distribution = {};

          for (let i = 0; i <= maxDelai; i += pas) {
            const min = i;
            const max = i + pas;
            const label = `${min}-${max}`;
            distribution[label] = delais.filter(d => d >= min && d < max).length;
          }

          const tableData = Object.entries(distribution).map(([classe, count]) => [
            classe,
            count,
            ((count / delais.length) * 100).toFixed(1) + '%'
          ]);

          doc.autoTable({
            startY: y,
            head: [['Classe (jours)', 'Effectif', 'Pourcentage']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: [220, 38, 38] },
            styles: { fontSize: 9 }
          });

          y = doc.lastAutoTable.finalY + 10;

          // 4. CAUSES SPÉCIALES
          if (y > 250) {
            doc.addPage();
            y = 20;
          }

          doc.setFontSize(14);
          doc.setTextColor(220, 38, 38);
          doc.text('4. CAUSES SPÉCIALES', 20, y);
          y += 10;

          const pointsHorsLimites = delais.filter(d => d > UCL || d < LCL).length;

          if (pointsHorsLimites > 0) {
            doc.setFontSize(11);
            doc.setTextColor(220, 38, 38);
            doc.text(`⚠ ${pointsHorsLimites} point(s) hors limites de contrôle`, 30, y);
            y += 7;

            // Lister les valeurs aberrantes
            const valeursAberrantes = delais
              .map((d, i) => ({ index: i + 1, valeur: d }))
              .filter(item => item.valeur > UCL || item.valeur < LCL)
              .slice(0, 10);

            doc.autoTable({
              startY: y,
              head: [['N°', 'Délai (jours)', 'Statut']],
              body: valeursAberrantes.map(item => [
                item.index,
                item.valeur.toFixed(0),
                item.valeur > UCL ? 'Hors limite supérieure' : 'Hors limite inférieure'
              ]),
              theme: 'striped',
              headStyles: { fillColor: [220, 38, 38] },
              styles: { fontSize: 9 }
            });
          } else {
            doc.setFontSize(11);
            doc.setTextColor(34, 197, 94);
            doc.text('✓ Aucune cause spéciale détectée', 30, y);
          }
        }
      }

      // Sauvegarder le PDF
      doc.save(`SIMPLON_SPC_${date.replace(/\//g, '-')}.pdf`);
      toast.success('Rapport SPC PDF généré !');
    } catch (error) {
      console.error('Erreur génération rapport SPC:', error);
      toast.error('Erreur lors de la génération du rapport SPC');
    }
  };

  // Fonction pour générer le Bilan Mensuel PDF avec période personnalisée
const generateMensuelPDF = async (startDate, endDate) => {
  try {
    const [apprenantsResponse, configResponse] = await Promise.all([
      apprenantsAPI.getAll({ limit: 1000 }),
      configAPI.get()
    ]);

    const tousApprenants = apprenantsResponse.data.apprenants || [];
    const config = configResponse.data || {};
    const positifs = config.statutsInsertion?.positif || [];
    const negatifs = config.statutsInsertion?.negatif || [];

    const debut = startDate ? new Date(startDate) : null;
    const fin = endDate ? new Date(endDate + 'T23:59:59') : null;

    const inRange = (dateStr) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      if (debut && d < debut) return false;
      if (fin && d > fin) return false;
      return true;
    };

    // Formés sur la période (filtrés par dateFin)
    const formes = tousApprenants.filter(a => inRange(a.dateFin));

    // Insérés sur la période (filtrés par dateInsertion)
    const inseres = tousApprenants.filter(a =>
      inRange(a.dateInsertion) && positifs.includes(a.statutInsertion)
    );

    const doc = new jsPDF();
    const date = format(new Date(), 'dd/MM/yyyy');
    const periodeText = startDate && endDate
      ? `Période du ${format(new Date(startDate), 'dd/MM/yyyy')} au ${format(new Date(endDate), 'dd/MM/yyyy')}`
      : 'Toutes les périodes';

    // ── En-tête ──────────────────────────────────────────
    doc.setFontSize(18);
    doc.setTextColor(220, 38, 38);
    doc.text("SIMPLON Côte d'Ivoire", 105, 20, { align: 'center' });
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text('BILAN MENSUEL', 105, 28, { align: 'center' });
    doc.setFontSize(10);
    doc.text(periodeText, 105, 35, { align: 'center' });
    doc.setFontSize(9);
    doc.text('Généré le : ' + date, 105, 42, { align: 'center' });
    doc.setDrawColor(220, 38, 38);
    doc.line(20, 47, 190, 47);

    let y = 55;

    // ── 1. KPIs ───────────────────────────────────────────
    doc.setFontSize(14);
    doc.setTextColor(220, 38, 38);
    doc.text('1. INDICATEURS CLÉS', 20, y);
    y += 8;

    const totalFormes = formes.length;
    const totalInseres = inseres.length;
    const femmesFormes = formes.filter(a => a.genre === 'Femme').length;
    const femmesInseres = inseres.filter(a => a.genre === 'Femme').length;
    const tauxFemmesFormes = totalFormes > 0 ? ((femmesFormes / totalFormes) * 100).toFixed(1) : '0.0';
    const tauxFemmesInseres = totalInseres > 0 ? ((femmesInseres / totalInseres) * 100).toFixed(1) : '0.0';
    const tauxInsertion = totalFormes > 0 ? ((totalInseres / totalFormes) * 100).toFixed(1) : '0.0';

    const delais = inseres
      .filter(a => a.dateFin && a.dateInsertion)
      .map(a => Math.round((new Date(a.dateInsertion) - new Date(a.dateFin)) / 86400000))
      .filter(d => d >= 0)
      .sort((a, b) => a - b);
    const delaiMedian = delais.length > 0
      ? delais[Math.floor(delais.length / 2)] + ' jours'
      : 'N/A';

    doc.setFontSize(10);
    doc.setTextColor(0);

    const kpis = [
      ['Apprenants formés sur la période', totalFormes],
      ['Apprenants insérés sur la période', totalInseres],
      ['Taux d\'insertion', tauxInsertion + '%'],
      ['% Femmes formées', tauxFemmesFormes + '%'],
      ['% Femmes insérées', tauxFemmesInseres + '%'],
      ['Délai médian d\'insertion', delaiMedian],
    ];

    const midKpi = Math.ceil(kpis.length / 2);
    kpis.forEach(([label, value], i) => {
      const col = i < midKpi ? 30 : 115;
      const row = y + (i < midKpi ? i : i - midKpi) * 7;
      doc.text(`${label} : ${value}`, col, row);
    });
    y += midKpi * 7 + 10;

    // ── 2. TABLEAU FORMÉS + INSÉRÉS ───────────────────────
    doc.setFontSize(14);
    doc.setTextColor(220, 38, 38);
    doc.text('2. APPRENANTS FORMÉS ET INSÉRÉS', 20, y);
    y += 8;

    // Construire les lignes fusionnées (Formé / Inséré)
    // On déduplique : si un apprenant est dans les deux listes, on l'affiche une seule fois avec statut "Formé + Inséré"
    const formesIds = new Set(formes.map(a => a._id));
    const inseresIds = new Set(inseres.map(a => a._id));

    const allIds = new Set([...formesIds, ...inseresIds]);
    const apprenantsMap = {};
    tousApprenants.forEach(a => { apprenantsMap[a._id] = a; });

    const tableRows = [...allIds].map(id => {
      const a = apprenantsMap[id];
      const estForme = formesIds.has(id);
      const estInsere = inseresIds.has(id);
      const statut = estForme && estInsere ? 'Formé + Inséré' : estForme ? 'Formé' : 'Inséré';

      return [
        a.nom || '-',
        a.prenom || '-',
        a.genre || '-',
        a.typeFormation || '-',
        a.dateFin ? format(new Date(a.dateFin), 'dd/MM/yyyy') : '-',
        a.dateInsertion ? format(new Date(a.dateInsertion), 'dd/MM/yyyy') : '-',
        a.entreprise || '-',
        statut
      ];
    });

    // Trier : Formé + Inséré en premier, puis Inséré, puis Formé
    const statutOrder = { 'Formé + Inséré': 0, 'Inséré': 1, 'Formé': 2 };
    tableRows.sort((a, b) => statutOrder[a[7]] - statutOrder[b[7]]);

    if (tableRows.length > 0) {
      doc.autoTable({
        startY: y,
        head: [['Nom', 'Prénom', 'Genre', 'Formation', 'Fin formation', 'Date insertion', 'Entreprise', 'Statut']],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [220, 38, 38], fontSize: 8 },
        styles: { fontSize: 7.5 },
        didParseCell: (data) => {
          if (data.column.index === 7 && data.section === 'body') {
            const val = data.cell.raw;
            if (val === 'Formé + Inséré') data.cell.styles.textColor = [22, 163, 74];
            else if (val === 'Inséré') data.cell.styles.textColor = [37, 99, 235];
            else data.cell.styles.textColor = [100, 100, 100];
          }
        },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 22 },
          2: { cellWidth: 14 },
          3: { cellWidth: 28 },
          4: { cellWidth: 22 },
          5: { cellWidth: 22 },
          6: { cellWidth: 28 },
          7: { cellWidth: 22 },
        }
      });
      y = doc.lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(11);
      doc.setTextColor(100);
      doc.text('Aucun apprenant sur cette période', 30, y);
      y += 15;
    }

    // ── 3. RÉPARTITION PAR TYPE DE FORMATION (sur les formés) ──
    if (y > 230) { doc.addPage(); y = 20; }

    doc.setFontSize(14);
    doc.setTextColor(220, 38, 38);
    doc.text('3. RÉPARTITION PAR TYPE DE FORMATION (formés)', 20, y);
    y += 8;

    const typesMap = {};
    formes.forEach(a => {
      const type = a.typeFormation || 'Non spécifié';
      if (!typesMap[type]) typesMap[type] = { total: 0, femmes: 0, inseres: 0 };
      typesMap[type].total++;
      if (a.genre === 'Femme') typesMap[type].femmes++;
      if (inseresIds.has(a._id)) typesMap[type].inseres++;
    });

    const typeData = Object.entries(typesMap).map(([type, d]) => [
      type,
      d.total,
      d.femmes,
      d.total > 0 ? ((d.femmes / d.total) * 100).toFixed(1) + '%' : '0%',
      d.inseres,
      d.total > 0 ? ((d.inseres / d.total) * 100).toFixed(1) + '%' : '0%',
    ]);

    doc.autoTable({
      startY: y,
      head: [['Type de formation', 'Total', 'Femmes', '% F', 'Insérés', 'Taux insertion']],
      body: typeData,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 38] },
      styles: { fontSize: 8 }
    });
    y = doc.lastAutoTable.finalY + 10;

    // ── 4. STATUTS DE SORTIE (sur les formés) ──────────────
    if (y > 230) { doc.addPage(); y = 20; }

    doc.setFontSize(14);
    doc.setTextColor(220, 38, 38);
    doc.text('4. STATUTS DE SORTIE (formés)', 20, y);
    y += 8;

    let positif = 0, negatif = 0, autre = 0;
    formes.forEach(a => {
      if (positifs.includes(a.statutInsertion)) positif++;
      else if (negatifs.includes(a.statutInsertion)) negatif++;
      else autre++;
    });

    const sortieData = [
      ['Positif', positif, totalFormes > 0 ? ((positif / totalFormes) * 100).toFixed(1) + '%' : '0%'],
      ['Négatif', negatif, totalFormes > 0 ? ((negatif / totalFormes) * 100).toFixed(1) + '%' : '0%'],
      ['Autre / En cours', autre, totalFormes > 0 ? ((autre / totalFormes) * 100).toFixed(1) + '%' : '0%'],
    ].filter(r => r[1] > 0);

    doc.autoTable({
      startY: y,
      head: [['Statut de sortie', 'Effectif', 'Pourcentage']],
      body: sortieData,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 38] },
      styles: { fontSize: 9 }
    });

    // ── Sauvegarde ────────────────────────────────────────
    doc.save(`SIMPLON_Bilan_Mensuel_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('Bilan mensuel PDF généré !');

  } catch (error) {
    console.error('Erreur génération bilan mensuel:', error);
    toast.error('Erreur lors de la génération du bilan mensuel');
  }
};
  // Fonction pour générer le rapport global
  const generateGlobalPDF = async () => {
  try {
    const [
      statsResponse,
      apprenantsResponse,
      alertesResponse,
      suiviStatsResponse,
      suivisResponse,
      configResponse
    ] = await Promise.all([
      statsAPI.get(),
      apprenantsAPI.getAll({ limit: 1000 }),
      alertesAPI.getAll(),
      statsAPI.getSuivi(),
      suivisAPI.getAll(),
      configAPI.get()
    ]);

    const stats       = statsResponse.data.general || {};
    const apprenants  = apprenantsResponse.data.apprenants || [];
    const alertes     = alertesResponse.data.alertes || [];
    const counts      = alertesResponse.data.counts || {};
    const suiviStats  = suiviStatsResponse.data || {};
    const suivis      = suivisResponse.data.suivis || [];
    const config      = configResponse.data || {};
    const positifs    = config.statutsInsertion?.positif || [];
    const negatifs    = config.statutsInsertion?.negatif || [];
    const delaiSuivi  = config.delaiSuivi || 90;

    const apprenantsDict = {};
    apprenants.forEach(a => { apprenantsDict[a._id] = a; });

    const doc  = new jsPDF();
    const date = format(new Date(), 'dd/MM/yyyy');

    // ── Fonction utilitaire page suivante ──────────────
    const checkPage = (currentY, needed = 40) => {
      if (currentY > 297 - needed) { doc.addPage(); return 20; }
      return currentY;
    };

    // ══════════════════════════════════════════════════
    // EN-TÊTE
    // ══════════════════════════════════════════════════
    doc.setFontSize(18);
    doc.setTextColor(220, 38, 38);
    doc.text("SIMPLON Côte d'Ivoire", 105, 20, { align: 'center' });
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text('Plateforme de Gestion des Insertions', 105, 28, { align: 'center' });
    doc.setFontSize(10);
    doc.text('RAPPORT GLOBAL — ' + date, 105, 35, { align: 'center' });
    doc.setDrawColor(220, 38, 38);
    doc.line(20, 40, 190, 40);

    let y = 50;

    // ══════════════════════════════════════════════════
    // SECTION 1 — STATISTIQUES GÉNÉRALES
    // ══════════════════════════════════════════════════
    doc.setFontSize(14); doc.setTextColor(220, 38, 38);
    doc.text('1. STATISTIQUES GÉNÉRALES', 20, y); y += 8;

    doc.setFontSize(10); doc.setTextColor(0);
    const generalKpis = [
      ['Total formés',        stats.total || 0],
      ['Formations Métiers',  stats.totalMetiers || 0],
      ['Acculturation',       stats.totalAcculturation || 0],
      ['Femmes',              `${stats.femmes || 0} (${stats.tauxFemmes || 0}%)`],
      ['Hommes',              stats.hommes || 0],
      ['Taux insertion',      `${stats.tauxInsertion || 0}%`],
      ['Sortie Positive',     `${stats.tauxPositif || 0}%`],
      ['Délai médian',        stats.delaiMedian ? `${Math.round(stats.delaiMedian)} jours` : 'N/A'],
    ];
    const mid = Math.ceil(generalKpis.length / 2);
    generalKpis.forEach(([label, value], i) => {
      const col = i < mid ? 30 : 115;
      const row = y + (i < mid ? i : i - mid) * 7;
      doc.text(`${label} : ${value}`, col, row);
    });
    y += mid * 7 + 12;

    // ── Répartition par type de formation ──
    y = checkPage(y, 50);
    const typesMap = {};
    apprenants.forEach(a => {
      const type = a.typeFormation || 'Non spécifié';
      if (!typesMap[type]) typesMap[type] = { total: 0, femmes: 0, inseres: 0 };
      typesMap[type].total++;
      if (a.genre === 'Femme') typesMap[type].femmes++;
      if (positifs.includes(a.statutInsertion)) typesMap[type].inseres++;
    });
    doc.autoTable({
      startY: y,
      head: [['Type de formation', 'Total', 'Femmes', '% F', 'Insérés', 'Taux']],
      body: Object.entries(typesMap).map(([type, d]) => [
        type, d.total, d.femmes,
        d.total > 0 ? ((d.femmes / d.total) * 100).toFixed(1) + '%' : '0%',
        d.inseres,
        d.total > 0 ? ((d.inseres / d.total) * 100).toFixed(1) + '%' : '0%',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 38] },
      styles: { fontSize: 8 }
    });
    y = doc.lastAutoTable.finalY + 10;

    // ── Statuts de sortie ──
    y = checkPage(y, 40);
    let positif = 0, negatif = 0, autre = 0;
    apprenants.forEach(a => {
      if (positifs.includes(a.statutInsertion)) positif++;
      else if (negatifs.includes(a.statutInsertion)) negatif++;
      else autre++;
    });
    doc.autoTable({
      startY: y,
      head: [['Statut de sortie', 'Effectif', 'Pourcentage']],
      body: [
        ['Positif',       positif, apprenants.length > 0 ? ((positif / apprenants.length) * 100).toFixed(1) + '%' : '0%'],
        ['Négatif',       negatif, apprenants.length > 0 ? ((negatif / apprenants.length) * 100).toFixed(1) + '%' : '0%'],
        ['Autre/En cours', autre,  apprenants.length > 0 ? ((autre  / apprenants.length) * 100).toFixed(1) + '%' : '0%'],
      ].filter(r => r[1] > 0),
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 38] },
      styles: { fontSize: 9 }
    });
    y = doc.lastAutoTable.finalY + 12;

    // // ══════════════════════════════════════════════════
    // // SECTION 2 — RAPPORT BAILLEURS
    // // ══════════════════════════════════════════════════
    // y = checkPage(y, 60);
    // doc.setFontSize(14); doc.setTextColor(220, 38, 38);
    // doc.text('2. RAPPORT BAILLEURS', 20, y); y += 8;

    // // Diplômes
    // doc.setFontSize(11); doc.setTextColor(60);
    // doc.text('Répartition par diplôme', 22, y); y += 5;
    // const diplomes = {};
    // apprenants.forEach(a => {
    //   const d = a.diplome || 'Non précisé';
    //   diplomes[d] = (diplomes[d] || 0) + 1;
    // });
    // doc.autoTable({
    //   startY: y,
    //   head: [['Diplôme', 'Effectif', '%']],
    //   body: Object.entries(diplomes)
    //     .sort((a, b) => b[1] - a[1])
    //     .map(([d, n]) => [d, n, ((n / apprenants.length) * 100).toFixed(1) + '%']),
    //   theme: 'striped',
    //   headStyles: { fillColor: [220, 38, 38] },
    //   styles: { fontSize: 8 }
    // });
    // y = doc.lastAutoTable.finalY + 8;

    // // Statuts d'entrée
    // y = checkPage(y, 40);
    // doc.setFontSize(11); doc.setTextColor(60);
    // doc.text('Statuts d\'entrée', 22, y); y += 5;
    // const statutsEntree = {};
    // apprenants.forEach(a => {
    //   const s = a.statutEntree || 'Non précisé';
    //   statutsEntree[s] = (statutsEntree[s] || 0) + 1;
    // });
    // doc.autoTable({
    //   startY: y,
    //   head: [['Statut d\'entrée', 'Effectif', '%']],
    //   body: Object.entries(statutsEntree)
    //     .map(([s, n]) => [s, n, ((n / apprenants.length) * 100).toFixed(1) + '%']),
    //   theme: 'striped',
    //   headStyles: { fillColor: [220, 38, 38] },
    //   styles: { fontSize: 8 }
    // });
    // y = doc.lastAutoTable.finalY + 12;

    // ══════════════════════════════════════════════════
    // SECTION 3 — RAPPORT SUIVI POST-INSERTION
    // ══════════════════════════════════════════════════
    y = checkPage(y, 60);
    doc.setFontSize(14); doc.setTextColor(220, 38, 38);
    doc.text('2. SUIVI POST-INSERTION', 20, y); y += 8;

    doc.setFontSize(10); doc.setTextColor(0);
    [
      ['Suivis effectués', suiviStats.suiviOK  || 0],
      ['En attente',       suiviStats.enAttente || 0],
      ['En retard',        suiviStats.enRetard  || 0],
      ['Note moyenne',     suiviStats.noteMoyenne ? suiviStats.noteMoyenne + '/5' : 'N/A'],
      ['Délai configuré',  delaiSuivi + ' jours'],
    ].forEach(([label, value]) => {
      doc.text(`${label} : ${value}`, 30, y); y += 7;
    });
    y += 4;

    // Apprenants à contacter
    y = checkPage(y, 40);
    const maintenant = new Date();
    const aContacter = apprenants.filter(a => {
      if (!a.dateInsertion || !positifs.includes(a.statutInsertion)) return false;
      const jours = Math.floor((maintenant - new Date(a.dateInsertion)) / 86400000);
      const aUnSuivi = suivis.some(s => {
        const id = s.apprenantId?._id || s.apprenantId;
        return id === a._id;
      });
      return !aUnSuivi && jours >= delaiSuivi;
    });

    if (aContacter.length > 0) {
      doc.setFontSize(11); doc.setTextColor(60);
      doc.text('Apprenants à contacter', 22, y); y += 5;
      doc.autoTable({
        startY: y,
        head: [['Nom', 'Prénom', 'Téléphone', 'Entreprise', 'Date insertion']],
        body: aContacter.slice(0, 10).map(a => [
          a.nom || '-', a.prenom || '-', a.telephone || '-',
          a.entreprise || '-',
          a.dateInsertion ? format(new Date(a.dateInsertion), 'dd/MM/yyyy') : '-'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [220, 38, 38] },
        styles: { fontSize: 8 }
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // Suivis enregistrés
    y = checkPage(y, 40);
    const suivisData = [];
    suivis.forEach(suivi => {
      let apprenant = typeof suivi.apprenantId === 'object'
        ? apprenantsDict[suivi.apprenantId._id] || suivi.apprenantId
        : apprenantsDict[suivi.apprenantId];

      if (suivi.suivisMultiNiveaux?.length > 0) {
        suivi.suivisMultiNiveaux.forEach(n => suivisData.push([
          apprenant?.nom || '-', apprenant?.prenom || '-',
          n.niveau || 'Multi',
          n.dateContact ? format(new Date(n.dateContact), 'dd/MM/yyyy') : '-',
          n.note || '-', n.appreciation || '-'
        ]));
      } else {
        suivisData.push([
          apprenant?.nom || '-', apprenant?.prenom || '-', 'Simple',
          suivi.date ? format(new Date(suivi.date), 'dd/MM/yyyy') : '-',
          suivi.note || '-', suivi.appreciation || '-'
        ]);
      }
    });

    if (suivisData.length > 0) {
      doc.setFontSize(11); doc.setTextColor(60);
      doc.text('Suivis enregistrés', 22, y); y += 5;
      doc.autoTable({
        startY: y,
        head: [['Nom', 'Prénom', 'Type', 'Date', 'Note', 'Appréciation']],
        body: suivisData.slice(0, 20),
        theme: 'striped',
        headStyles: { fillColor: [220, 38, 38] },
        styles: { fontSize: 8 }
      });
      y = doc.lastAutoTable.finalY + 12;
    }

    // ══════════════════════════════════════════════════
    // SECTION 4 — RAPPORT SPC
    // ══════════════════════════════════════════════════
    y = checkPage(y, 60);
    doc.setFontSize(14); doc.setTextColor(220, 38, 38);
    doc.text('3. CONTRÔLE STATISTIQUE (SPC)', 20, y); y += 8;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Uniquement sur formations métiers', 22, y); y += 7;

    const metiers = apprenants.filter(a =>
      a.typeFormation && !a.typeFormation.toLowerCase().includes('acculturation')
      && a.dateFin && a.dateInsertion
    );

    if (metiers.length < 30) {
      doc.setFontSize(10); doc.setTextColor(0);
      doc.text(`Données insuffisantes (${metiers.length}/30 observations requises)`, 30, y);
      y += 12;
    } else {
      const delais = metiers
        .map(a => Math.round((new Date(a.dateInsertion) - new Date(a.dateFin)) / 86400000))
        .filter(d => d >= 0);

      const moyenne   = delais.reduce((a, b) => a + b, 0) / delais.length;
      const ecartType = Math.sqrt(delais.reduce((s, d) => s + Math.pow(d - moyenne, 2), 0) / delais.length);
      const UCL       = moyenne + 3 * ecartType;
      const LCL       = Math.max(0, moyenne - 3 * ecartType);
      const USL       = config?.USL || 90;
      const Cpk       = Math.min((USL - moyenne) / (3 * ecartType), (moyenne - 0) / (3 * ecartType));

      doc.setFontSize(10); doc.setTextColor(0);
      [
        ['Observations',      delais.length],
        ['Moyenne',           moyenne.toFixed(1) + ' jours'],
        ['Écart-type',        ecartType.toFixed(1) + ' jours'],
        ['UCL',               UCL.toFixed(1) + ' jours'],
        ['LCL',               LCL.toFixed(1) + ' jours'],
        ['USL (objectif)',    USL + ' jours'],
        ['Cpk',               Cpk.toFixed(2)],
      ].forEach(([label, value]) => {
        doc.text(`${label} : ${value}`, 30, y); y += 7;
      });

      // Interprétation Cpk
      doc.setFontSize(10);
      if (Cpk >= 1.33)      { doc.setTextColor(34, 197, 94);  doc.text('✓ Processus capable (Cpk ≥ 1.33)', 30, y); }
      else if (Cpk >= 1.0)  { doc.setTextColor(245, 158, 11); doc.text('⚠ Marginalement capable (1.0 ≤ Cpk < 1.33)', 30, y); }
      else                  { doc.setTextColor(220, 38, 38);  doc.text('✗ Processus non capable (Cpk < 1.0)', 30, y); }
      y += 12;
    }

    // ══════════════════════════════════════════════════
    // SECTION 5 — RAPPORT ALERTES
    // ══════════════════════════════════════════════════
    y = checkPage(y, 60);
    doc.setFontSize(14); doc.setTextColor(220, 38, 38);
    doc.text('4. ALERTES', 20, y); y += 8;

    doc.setFontSize(10); doc.setTextColor(0);
    [
      ['Critiques',    counts.critique || 0],
      ['Relances',     counts.relance  || 0],
      ['Suivi 3 mois', counts.suivi    || 0],
      ['Vérifications',counts.verif    || 0],
      ['Total',        alertes.length],
    ].forEach(([label, value]) => {
      doc.text(`${label} : ${value}`, 30, y); y += 7;
    });
    y += 4;

    if (alertes.length > 0) {
      y = checkPage(y, 40);
      doc.autoTable({
        startY: y,
        head: [['Type', 'Titre', 'Message', 'Nom', 'Prénom']],
        body: alertes.map(al => {
          const app = apprenantsDict[al.apprenantId] || {};
          return [
            al.type === 'critique' ? 'Critique'
              : al.type === 'relance' ? 'Relance'
              : al.type === 'suivi'   ? 'Suivi 3 mois' : 'Vérif.',
            al.titre || '-',
            al.message || al.msg || '-',
            app.nom    || al.apprenantNom || '-',
            app.prenom || '-'
          ];
        }),
        theme: 'striped',
        headStyles: { fillColor: [220, 38, 38] },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 22 }, 1: { cellWidth: 30 },
          2: { cellWidth: 60 }, 3: { cellWidth: 30 }, 4: { cellWidth: 28 }
        }
      });
    }

    // ── Sauvegarde ────────────────────────────────────
    doc.save(`SIMPLON_Rapport_Global_${date.replace(/\//g, '-')}.pdf`);
    toast.success('Rapport Global PDF généré !');

  } catch (error) {
    console.error('Erreur génération rapport global:', error);
    toast.error('Erreur lors de la génération du rapport global');
  }
};

  // Fonction pour ouvrir le modal de sélection de dates pour le bilan mensuel
  const openMensuelDateModal = () => {
    // Par défaut, pré-remplir avec le mois en cours
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setStartDate(format(firstDay, 'yyyy-MM-dd'));
    setEndDate(format(lastDay, 'yyyy-MM-dd'));
    setShowDateModal(true);
  };

  // Fonction pour générer le PDF avec les dates sélectionnées
  const handleGenerateMensuelWithDates = () => {
    if (!startDate || !endDate) {
      toast.error('Veuillez sélectionner une période');
      return;
    }
    setShowDateModal(false);
    generateMensuelPDF(startDate, endDate);
  };

  const generatePDF = async (type) => {
    if (type === 'global') {
      await generateGlobalPDF();
    } else if (type === 'alertes') {
      await generateAlertesPDF();
    } else if (type === 'suivi') {
      await generateSuiviPDF();
    } else if (type === 'spc') {
      await generateSPCPDF();
    } else if (type === 'mensuel') {
      // Ouvrir le modal de sélection de dates
      openMensuelDateModal();
    } else {
      // Rapports standards (bailleurs, etc.)
      try {
        const [statsResponse, apprenantsResponse] = await Promise.all([
          statsAPI.get(),
          apprenantsAPI.getAll({ limit: 1000 })
        ]);

        const stats = statsResponse.data.general;
        const doc = new jsPDF();
        const date = format(new Date(), 'dd/MM/yyyy');

        // En-tête
        doc.setFontSize(18);
        doc.setTextColor(220, 38, 38);
        doc.text('SIMPLON Côte d\'Ivoire', 105, 20, { align: 'center' });
        doc.setFontSize(12);
        doc.setTextColor(100);
        doc.text('Plateforme de Gestion des Insertions', 105, 28, { align: 'center' });
        doc.setFontSize(10);
        doc.text('Généré le: ' + date, 105, 35, { align: 'center' });
        doc.setDrawColor(220, 38, 38);
        doc.line(20, 40, 190, 40);

        let y = 50;
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('RAPPORT ' + type.toUpperCase(), 105, y, { align: 'center' });
        y += 15;

        doc.setFontSize(11);
        doc.text('Total formés: ' + (stats.total || 0), 25, y);
        y += 7;
        doc.text('  - Formations Métiers: ' + (stats.totalMetiers || 0), 25, y);
        y += 7;
        doc.text('  - Acculturation: ' + (stats.totalAcculturation || 0), 25, y);
        y += 10;
        doc.text('Femmes: ' + (stats.femmes || 0) + ' (' + (stats.tauxFemmes || 0) + '%)', 25, y);
        y += 7;
        doc.text('Taux insertion: ' + (stats.tauxInsertion || 0) + '%', 25, y);
        y += 7;
        doc.text('Sortie Positive: ' + (stats.tauxPositif || 0) + '%', 25, y);
        y += 7;
        doc.text('Délai médian: ' + (stats.delaiMedian ? Math.round(stats.delaiMedian) + ' jours' : 'N/A'), 25, y);

        doc.save(`SIMPLON_${type}_${date.replace(/\//g, '-')}.pdf`);
        toast.success('PDF généré !');
      } catch (error) {
        toast.error('Erreur lors de la génération du PDF');
      }
    }
  };

  const exportExcel = async () => {
    try {
      const response = await apprenantsAPI.getAll({ limit: 1000 });
      const data = (response.data.apprenants || []).map(a => ({
        Nom: a.nom,
        Prénom: a.prenom,
        Genre: a.genre,
        Âge: a.age,
        Téléphone: a.telephone,
        Email: a.email,
        Filière: a.filiere,
        'Type Formation': a.typeFormation,
        'Statut Insertion': a.statutInsertion,
        Entreprise: a.entreprise,
        Poste: a.poste
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Apprenants');
      XLSX.writeFile(wb, `SIMPLON_CI_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      toast.success('Excel exporté !');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const exportJSON = async () => {
    try {
      const response = await apprenantsAPI.getAll({ limit: 1000 });
      const data = JSON.stringify({
        apprenants: response.data.apprenants,
        exportDate: new Date().toISOString()
      }, null, 2);

      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SIMPLON_CI_Backup_${format(new Date(), 'yyyy-MM-dd')}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('JSON exporté !');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const rapportTypes = [
    { type: 'global', title: 'Rapport Global', icon: 'chart', desc: 'Synthèse complète de tous les rapports' },
    { type: 'mensuel', title: 'Bilan Mensuel', icon: 'calendar', desc: 'Mois en cours' },
    { type: 'bailleurs', title: 'Rapport Bailleurs', icon: 'building', desc: 'Partenaires' },
    { type: 'spc', title: 'Rapport SPC', icon: 'spc', desc: 'Contrôle statistique' },
    { type: 'suivi', title: 'Rapport Suivi', icon: 'suivi', desc: 'Post-Insertion' },
    { type: 'alertes', title: 'Rapport Alertes', icon: 'alertes', desc: 'Actions requises' }
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
        <Icon name="rapports" size={28} className="mr-3 text-red-600" />
        Rapports
      </h2>

      {/* Cartes de Rapports */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {rapportTypes.map(rapport => (
          <div
            key={rapport.type}
            className="section-card bg-white rounded-xl p-6 hover:shadow-xl cursor-pointer transition-all"
            onClick={() => generatePDF(rapport.type)}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-red-100">
                <Icon name={rapport.icon} size={32} className="text-red-600" />
              </div>
              <h3 className="font-bold text-gray-800">{rapport.title}</h3>
              <p className="text-sm text-gray-500">{rapport.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de sélection de période pour le Bilan Mensuel */}
      <Modal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        title="Sélectionner la période du bilan mensuel"
        size="md"
      >
        <div className="p-4">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowDateModal(false)}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleGenerateMensuelWithDates}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Générer le rapport
            </button>
          </div>
        </div>
      </Modal>

      {/* Export/Import */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="section-card bg-white rounded-xl p-6">
          <h3 className="font-bold mb-4 flex items-center text-gray-800">
            <Icon name="download" size={20} className="mr-2 text-red-600" />
            Exporter
          </h3>
          <div className="space-y-3">
            <button
              onClick={exportExcel}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Icon name="download" size={18} />
              <span>Excel</span>
            </button>
            <button
              onClick={exportJSON}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Icon name="download" size={18} />
              <span>JSON</span>
            </button>
            <button
              onClick={exportJSON}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Icon name="download" size={18} />
              <span>Backup</span>
            </button>
          </div>
        </div>
        <div className="section-card bg-white rounded-xl p-6">
          <h3 className="font-bold mb-4 flex items-center text-gray-800">
            <Icon name="upload" size={20} className="mr-2 text-red-600" />
            Importer
          </h3>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer">
            <input
              id="importFile"
              name="importFile"
              type="file"
              accept=".json,.xlsx"
              className="hidden"
              aria-label="Fichier à importer"
              onChange={(e) => {
                toast.info('Fonctionnalité à venir');
              }}
            />
            <label htmlFor="importFile" className="cursor-pointer">
              <Icon name="upload" size={48} className="mx-auto mb-2 text-gray-400" />
              <p className="text-gray-600">Cliquez pour importer</p>
            </label>
          </div>
          <button
            onClick={() => toast.info('Fonctionnalité à venir')}
            className="w-full flex items-center justify-center gap-2 mt-4 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
          >
            <Icon name="download" size={18} />
            <span>Modèle</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Rapports;